import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import type { DamageTypeWithLineItems } from '@/common/damage';
import { ROLE } from '@/common/role';
import type { WithDeletedBy } from '@/common/soft-delete';
import type { WorkPackage } from '@/common/work-package';
import { type WorkPackageItem, WorkPackageStatus, workPackageStatusLabel } from '@/common/work-package';
import { DeleteButton } from '@/components/DeleteButton';
import { Restricted } from '@/components/Restricted';
import { DeleteConfirmDialog } from '@/components/soft-delete/DeleteConfirmDialog';
import { RestoreItemButton } from '@/components/soft-delete/RestoreItemButton';
import { DeletedItemBanner } from '@/components/soft-delete/DeletedItemBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ReadOnlySubtree } from '@/context/AssetReadOnlyContext';
import { useDeletedPreview } from '@/hooks/use-deleted-preview';
import { usePermission } from '@/hooks/use-permission';
import { useSoftDeleteItem } from '@/hooks/use-soft-delete';
import { softDeleteTargets } from '@/queries/soft-delete/soft-delete';
import { addWorkPackageOptions } from '@/queries/work-packages/add-work-package';
import { getWorkPackageOptions } from '@/queries/work-packages/get-work-package';
import { updateWorkPackageOptions } from '@/queries/work-packages/update-work-package';
import { DamagesListView } from './damage/DamagesListView';
import { WorkPackageItemsTable } from './table/WorkPackageItemsTable';

export function WorkPackageDetail() {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [activeTab, setActiveTab] = useState<string>('cost');

	const { workPackageId, assetId } = useParams<{ workPackageId: string; assetId: string }>();
	// A deleted package opened from the deleted list: read-only, with the lines deleted along (GH #331)
	const isDeleted = useDeletedPreview('workPackage');
	const hasUpdatePermission = usePermission(ROLE.WORK_PACKAGES_UPDATE) && !isDeleted;

	const isNew = workPackageId === 'new';

	const [title, setTitle] = useState<string>(isNew ? t('finance.workPackage.newTitle') : '');
	const [workPackageStatus, setWorkPackageStatus] = useState<WorkPackageStatus>(WorkPackageStatus.Pending);
	const [vatRate, setVatRate] = useState(19);
	const [lineItems, setLineItems] = useState([] as WorkPackageItem[]);
	const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
	const handleTitleClick = () => {
		if (!hasUpdatePermission) return;
		setIsEditingTitle(true);
	};
	const handleTitleBlur = () => {
		if (!hasUpdatePermission) return;
		setIsEditingTitle(false);
		if (!title.trim()) {
			setTitle(isNew ? t('finance.workPackage.newTitle') : t('finance.workPackage.defaultTitle'));
		}
	};

	const {
		isLoading,
		isError,
		data: workPackage,
		refetch: refetchDetails,
		isRefetching,
	} = useQuery(getWorkPackageOptions(workPackageId as string, !isNew, { deleted: isDeleted }));

	// Delete (GH #331); the toast offers Undo
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);
	const deleteWorkPackageApi = useSoftDeleteItem({
		onDeleted: () => {
			setIsDeleteOpen(false);
			navigate(`/assets/${assetId}/finance`);
		},
	});

	const addWorkPackageApi = useMutation(addWorkPackageOptions());
	const updateWorkPackageApi = useMutation(updateWorkPackageOptions());

	const onSave = async () => {
		if (!hasUpdatePermission) return;
		const mappedItems: WorkPackageItem[] = lineItems.map((item) => ({
			id: item.id,
			description: item.description,
			parentItemId: item.parentItemId,
			quantity: Number(item.quantity || 0),
			unitPrice: Number(item.unitPrice || 0) * 100,
			damageId: item.damageId,
			unitType: item.unitType,
		}));
		// Save logic here
		if (isNew) {
			await addWorkPackageApi.mutateAsync({
				title,
				status: workPackageStatus,
				vat: vatRate,
				assetId: assetId as string,
				workPackageItems: mappedItems,
			});
			// After adding, navigate back to the finance overview
			navigate(`/assets/${assetId}/finance`);
		} else {
			await updateWorkPackageApi.mutateAsync({
				id: workPackageId as string,
				payload: {
					title,
					status: workPackageStatus,
					vat: vatRate,
					assetId: assetId as string,
					workPackageItems: mappedItems,
				},
			});
			refetchDetails();
		}
	};

	// Populate form when work package data is loaded
	useEffect(() => {
		if (workPackage) {
			setTitle(workPackage.title || '');
			setWorkPackageStatus(workPackage.status || WorkPackageStatus.Pending);
			setVatRate(workPackage.vat || 19);
			const convertedItems = (workPackage.workPackageItems || []).map((item) => ({
				...item,
				quantity: Number(item.quantity || 0),
				unitPrice: Number(item.unitPrice || 0) / 100,
				totalPrice: Number(item.totalPrice || 0) / 100,
			}));
			setLineItems(convertedItems);
		}
	}, [workPackage]);

	const linkedDamages = useMemo(() => {
		if (lineItems.length === 0) return [] as DamageTypeWithLineItems[];
		const damageMap = {} as { [key: string]: boolean };
		return lineItems.reduce<DamageTypeWithLineItems[]>((acc, item) => {
			if (item.damage?.id && !damageMap[item.damage.id]) {
				damageMap[item.damage.id] = true;
				acc.push({
					...item.damage,
					lineItem: item,
				});
			}
			return acc;
		}, []);
	}, [lineItems]);

	const tabList = [
		{ value: 'cost', label: t('finance.workPackage.tabs.cost'), disabled: false },
		{ value: 'damages', label: t('finance.workPackage.tabs.damages'), disabled: linkedDamages.length === 0 },
	];

	if (isLoading) {
		return <div>{t('common.states.loading')}</div>;
	}
	// A deleted or unknown package: say so instead of showing an empty form
	if (!isNew && (isError || !workPackage)) {
		return (
			<div className="p-6 space-y-4">
				<Button variant="ghost" className="px-0!" onClick={() => navigate(`/assets/${assetId}/finance`)}>
					<ArrowLeft className="size-4 mr-1!" />
					{t('common.actions.backToList')}
				</Button>
				<p className="text-muted-foreground">{t('finance.workPackage.notFound')}</p>
			</div>
		);
	}

	const deletedPackage = isDeleted ? (workPackage as WithDeletedBy<WorkPackage> | null | undefined) : null;
	return (
		<ReadOnlySubtree active={isDeleted}>
			<div className="h-[calc(100vh-64px)] w-full flex flex-col bg-background text-foreground">
				<div className="flex-none bg-card text-card-foreground">
					<div className="p-6 pb-4">
						<Button
							variant="ghost"
							className="text-muted-foreground hover:text-primary hover:bg-transparent! px-0! mb-2 gap-0 hover:pl-2! cursor-pointer"
							onClick={() => navigate(`/assets/${assetId}/finance`)}
						>
							<ArrowLeft className="size-4 mr-1!" />
							{t('common.actions.backToList')}
						</Button>
						<div className="flex justify-between items-center mb-4 gap-2">
							<div className="flex-1">
								{isEditingTitle ? (
									<Input
										value={title}
										onChange={(e) => setTitle(e.target.value)}
										onBlur={handleTitleBlur}
										autoFocus
										className="text-2xl! font-bold bg-input w-full"
									/>
								) : (
									// biome-ignore lint/a11y/useKeyWithClickEvents: click needed
									<h1
										className="text-2xl font-bold cursor-pointer hover:text-blue-600 transition-colors px-2"
										onClick={handleTitleClick}
									>
										{title}
									</h1>
								)}
							</div>
							<div className="flex items-center gap-3">
								<Restricted to={ROLE.WORK_PACKAGES_UPDATE}>
									<div className="flex items-center gap-2">
										<span className="text-sm font-medium text-muted-foreground">{t('common.labels.status')}:</span>
										<Select
											value={workPackageStatus}
											onValueChange={(value) => setWorkPackageStatus(value as WorkPackageStatus)}
										>
											<SelectTrigger className="w-35 bg-input">
												<SelectValue placeholder={t('finance.workPackage.statusPlaceholder')} />
											</SelectTrigger>
											<SelectContent position="popper">
												{Object.values(WorkPackageStatus).map((status) => (
													<SelectItem key={status} value={status}>
														{workPackageStatusLabel(t, status)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</Restricted>
								{deletedPackage && workPackageId && (
									<RestoreItemButton
										target={softDeleteTargets.workPackage(workPackageId)}
										name={title || t('common.entities.workPackage', { count: 1 })}
										restoreRole={ROLE.WORK_PACKAGES_DELETE}
										onRestored={() =>
											navigate(`/assets/${assetId}/finance/work-packages/${workPackageId}`, { replace: true })
										}
										className="h-9 px-4"
									/>
								)}
								{!isNew && workPackageId && (
									<Restricted to={ROLE.WORK_PACKAGES_DELETE}>
										<DeleteButton size="default" onClick={() => setIsDeleteOpen(true)} />
										<DeleteConfirmDialog
											open={isDeleteOpen}
											onOpenChange={setIsDeleteOpen}
											target={softDeleteTargets.workPackage(workPackageId)}
											name={title || t('common.entities.workPackage', { count: 1 })}
											isDeleting={deleteWorkPackageApi.isPending}
											onConfirm={() =>
												deleteWorkPackageApi.mutate({
													target: softDeleteTargets.workPackage(workPackageId),
													name: title || t('common.entities.workPackage', { count: 1 }),
													successMessage: t('finance.toast.deleted'),
												})
											}
										/>
									</Restricted>
								)}
								<Button variant="outline" onClick={() => {}}>
									{t('common.actions.cancel')}
								</Button>
								<Button
									onClick={onSave}
									disabled={isLoading || isRefetching || updateWorkPackageApi.isPending || addWorkPackageApi.isPending}
									hidden={!hasUpdatePermission}
								>
									{updateWorkPackageApi.isPending || addWorkPackageApi.isPending
										? t('common.states.saving')
										: t('common.actions.save')}
								</Button>
							</div>
						</div>
					</div>
					{deletedPackage && workPackageId && (
						<DeletedItemBanner
							className="mx-6 mb-3"
							itemLabel={t('common.entities.workPackage', { count: 1 })}
							deletedAt={deletedPackage.deletedAt}
							deletedBy={deletedPackage.deletedBy}
						/>
					)}
					<Tabs value={activeTab} onValueChange={setActiveTab} className="overflow-hidden min-h-0 gap-0">
						<TabsList className="justify-start rounded-none border-b border-transparent p-0 bg-transparent">
							{tabList.map((tab) => (
								<TabsTrigger
									key={tab.value}
									value={tab.value}
									disabled={tab.disabled}
									className="rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary! data-[state=active]:bg-transparent data-[state=active]:text-primary! bg-transparent! text-muted-foreground hover:text-primary! hover:cursor-pointer px-4 py-2"
								>
									{tab.label}
								</TabsTrigger>
							))}
						</TabsList>
					</Tabs>
				</div>
				<div className="flex-1 overflow-auto bg-background p-4 m-0 border-t text-foreground">
					{activeTab === 'cost' && (
						<WorkPackageItemsTable
							lineItems={lineItems}
							setLineItems={setLineItems}
							vatRate={vatRate}
							setVatRate={setVatRate}
						/>
					)}
					{activeTab === 'damages' && <DamagesListView linkedDamages={linkedDamages} />}
				</div>
			</div>
		</ReadOnlySubtree>
	);
}
